/**
 * Сервис для работы с табелем часов
 */

import api from './api';
import {
  TimesheetEntry,
  GetTimesheetResponse,
  AddTimesheetEntryRequest,
  AddTimesheetEntryResponse,
  UpdateTimesheetEntryRequest,
  UpdateTimesheetEntryResponse,
} from '../types/timesheet';

/**
 * Получить все записи табеля часов для ресторана
 */
export const getTimesheetHours = async (restaurantId: string): Promise<TimesheetEntry[]> => {
  try {
    const response = await api.get<GetTimesheetResponse>(
      '/timesheet/hours',
      { restaurant_id: restaurantId },
      false // Не кешируем, чтобы всегда получать актуальные данные
    );
    return response.entries || [];
  } catch (error: any) {
    console.error('Error fetching timesheet hours:', error);
    throw new Error(
      error.response?.data?.error || 'Не удалось загрузить табель часов'
    );
  }
};

/**
 * Добавить новую запись в табель часов
 */
export const addTimesheetEntry = async (
  restaurantId: string,
  name: string,
  hours: string | number,
  manualAmount: number = 0
): Promise<AddTimesheetEntryResponse> => {
  try {
    const requestData: AddTimesheetEntryRequest = {
      restaurant_id: restaurantId,
      name,
      hours: typeof hours === 'string' ? hours : hours,
      manual_amount: manualAmount,
    };

    const response = await api.post<AddTimesheetEntryResponse>(
      '/timesheet/hours',
      requestData
    );

    return response;
  } catch (error: any) {
    console.error('Error adding timesheet entry:', error);
    throw new Error(
      error.response?.data?.error || 'Не удалось добавить запись в табель'
    );
  }
};

/**
 * Обновить существующую запись в табеле часов
 */
export const updateTimesheetEntry = async (
  restaurantId: string,
  sheetRow: number,
  name: string,
  hours: string | number,
  manualAmount: number = 0
): Promise<UpdateTimesheetEntryResponse> => {
  try {
    const requestData: UpdateTimesheetEntryRequest = {
      restaurant_id: restaurantId,
      name,
      hours: typeof hours === 'string' ? hours : hours,
      manual_amount: manualAmount,
    };

    const response = await api.put<UpdateTimesheetEntryResponse>(
      `/timesheet/hours/${sheetRow}`,
      requestData
    );

    return response;
  } catch (error: any) {
    console.error('Error updating timesheet entry:', error);
    throw new Error(
      error.response?.data?.error || 'Не удалось обновить запись в табеле'
    );
  }
};

/**
 * Удалить (очистить) запись из табеля часов
 */
export const deleteTimesheetEntry = async (
  restaurantId: string,
  sheetRow: number
): Promise<void> => {
  try {
    await api.delete(
      `/timesheet/hours/${sheetRow}?restaurant_id=${restaurantId}`
    );
  } catch (error: any) {
    console.error('Error deleting timesheet entry:', error);
    throw new Error(
      error.response?.data?.error || 'Не удалось удалить запись из табеля'
    );
  }
};

const timesheetService = {
  getTimesheetHours,
  addTimesheetEntry,
  updateTimesheetEntry,
  deleteTimesheetEntry,
};

export default timesheetService;

